import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Loader2, Send } from "lucide-react";
import { api } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const when = (iso) => new Date(iso).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function Consegne({ scope, user, dept }) {
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState(false);
  const load = useCallback(() => api.consegne(scope.tenant, scope.hotel, dept).then(setD).catch(() => setD(null)), [scope.tenant, scope.hotel, dept]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const canWrite = user.role !== "dipendente";
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.consegnaAdd({ tenant: scope.tenant, hotel: scope.hotel, department: dept, text }); toast.success("Consegne lasciate al turno dopo."); setText(""); await load(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const L = d.latest;
  return (
    <section className="mb-8 rounded-2xl border-2 border-brand-gold/50 bg-[#FFFBF2] p-5" data-testid="consegne">
      <div className="flex flex-wrap items-start gap-4">
        <span className="w-11 h-11 rounded-full bg-brand-gold/20 text-brand-gold flex items-center justify-center shrink-0"><ClipboardList size={20} /></span>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] font-bold tracking-[0.2em] text-brand-muted uppercase">Passaggio consegne</div>
          {L ? <>
            <p className="font-serif text-[20px] sm:text-[22px] text-brand-ink leading-snug mt-1 whitespace-pre-wrap" data-testid="consegne-latest">{L.text}</p>
            <div className="text-[11.5px] text-brand-muted mt-1" data-testid="consegne-latest-meta">{L.by_name} · {when(L.at)}</div>
          </> : <p className="text-[13px] text-brand-muted mt-1 italic" data-testid="consegne-empty">Nessuna consegna lasciata. {canWrite ? "Due righe a fine turno: chi arriva le trova qui." : "Il caporeparto le lascia qui a fine turno."}</p>}
          {d.items.length > 1 && <button type="button" onClick={() => setAll((v) => !v)} className="text-[11.5px] font-semibold text-brand-green mt-2" data-testid="consegne-history-toggle">{all ? "Nascondi" : `Consegne precedenti (${d.items.length - 1})`}</button>}
          {all && <ul className="mt-2 space-y-1.5 border-l-2 border-brand-gold/30 pl-3" data-testid="consegne-history">{d.items.slice(1).map((i) => <li key={i.id} className="text-[12.5px] text-brand-ink"><span className="text-brand-muted">{when(i.at)} · {i.by_name}:</span> {i.text}</li>)}</ul>}
        </div>
      </div>
      {canWrite && <form onSubmit={send} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="consegne-form">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={600} placeholder="Consegne per il turno dopo: cosa è rimasto in sospeso, cosa controllare per primo…" className="bg-white text-[13px] flex-1" data-testid="consegne-text" />
        <Button type="submit" disabled={busy || !text.trim()} className="h-11 sm:self-end bg-brand-green text-brand-cream text-[12px] font-semibold" data-testid="consegne-send">{busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Send size={13} className="mr-1" />} Lascia le consegne</Button>
      </form>}
    </section>
  );
}
